/**
 * UploadTray — GAL-8's persistent upload affordance, mounted once
 * in RootLayout so it lives in the CHROME, not in any gallery page.
 *
 * Collapsed: a small pill ("Uploading 14 / 40") floating just above
 * the Aperture bar (bottom-right, clear of the bar's center pill
 * and the editor's own floating action bars). Expanded: per-file
 * rows with stage/progress/failure detail — the same visual
 * language as the editor's old fixed overlay, which this replaces.
 *
 * Renders nothing when there's no upload activity, so the chrome
 * stays clean for everyone who isn't mid-batch.
 *
 * FIX-2 — adds the `stalled` stage (uploaded to Mux, transcode never
 * confirmed). It is rendered as amber "Unconfirmed" with the reason
 * spelled out, never as a green check: the manager used to report
 * those as successes after ~12 minutes of silence.
 */
import { useState } from 'react';
import { Upload, Check, X, ChevronDown, ChevronUp, Clock, RefreshCcw } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { NovaSpinner } from '@/components/nova';
import { useGalleryUploads } from '@/hooks/useGalleryUploads';
import { galleryUploads } from '@/lib/uploads/galleryUploadManager';
import type { UploadItemState } from '@/lib/uploads/galleryUploadEngine';

function StageLabel({ item }: { item: UploadItemState }) {
  switch (item.stage) {
    case 'queued':
      return <span className="text-[11px] text-muted-foreground">Queued</span>;
    case 'preparing':
      return (
        <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <NovaSpinner size={11} />
          Optimizing
        </span>
      );
    case 'uploading':
      return (
        <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <NovaSpinner size={11} />
          {item.progress > 0 ? `${Math.round(item.progress * 100)}%` : 'Uploading'}
          {item.attempt > 1 && <span>· retry {item.attempt}</span>}
        </span>
      );
    case 'saving':
      return (
        <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <NovaSpinner size={11} />
          Saving
        </span>
      );
    case 'processing':
      // FIX-2 — the message carries the "this is normal" copy that
      // grows more specific as a long transcode runs.
      return (
        <span
          className="inline-flex items-center gap-1.5 text-[11px] text-amber-400"
          title={item.message ?? 'Uploaded — Mux is transcoding.'}
        >
          <NovaSpinner size={11} />
          Processing…
        </span>
      );
    case 'stalled':
      // FIX-2 — uploaded, unconfirmed. NOT a check, NOT a red X.
      return (
        <span
          className="inline-flex items-center gap-1 text-[11px] text-amber-500"
          title={item.message}
        >
          <OrbitIcon name="clock" fallback={Clock} className="w-3 h-3" />
          Unconfirmed
        </span>
      );
    case 'done':
      return (
        <span className="inline-flex items-center gap-1 text-[11px] text-green-500">
          <OrbitIcon name="checkmark" fallback={Check} className="w-3 h-3" />
          Done
        </span>
      );
    case 'failed':
      return (
        <span
          className="inline-flex items-center gap-1 text-[11px] text-destructive"
          title={item.message}
        >
          <OrbitIcon name="xmark" fallback={X} className="w-3 h-3" />
          {item.message && item.message.length <= 32 ? item.message : 'Failed'}
        </span>
      );
  }
}

export function UploadTray() {
  const snapshot = useGalleryUploads();
  const [expanded, setExpanded] = useState(false);

  if (snapshot.batches.length === 0) return null;

  const active = snapshot.batches.some((b) => !b.settled);
  const failedTotal = snapshot.batches.reduce(
    (acc, b) => acc + b.items.filter((i) => i.stage === 'failed').length,
    0,
  );
  // FIX-2 — videos whose transcode we stopped waiting on. They are
  // neither successes nor failures, and the pill must not claim
  // "Uploads complete" while one of them is sitting there.
  const stalledTotal = snapshot.batches.reduce(
    (acc, b) => acc + b.items.filter((i) => i.stage === 'stalled').length,
    0,
  );
  const processingTotal = snapshot.batches.reduce(
    (acc, b) => acc + b.items.filter((i) => i.stage === 'processing').length,
    0,
  );
  // Everything settled and clean → nothing worth pinning to the
  // chrome; clear on the next tick-ish interaction instead of
  // auto-vanishing mid-glance.
  const allCleanAndSettled =
    !active && failedTotal === 0 && stalledTotal === 0 && processingTotal === 0;

  return (
    // bottom-24 clears the Aperture bar (RootLayout pads the shell
    // pb-24 for it); on sidebar-chrome setups the tray just floats
    // a little higher — harmless.
    <div className="fixed bottom-24 right-4 md:right-6 z-40 flex flex-col items-end gap-2">
      {expanded && (
        <div className="w-80 max-h-80 overflow-y-auto rounded-2xl border border-border/60 orbit-box-dark bg-card/95 backdrop-blur-md shadow-xl divide-y divide-border/40">
          {snapshot.batches.map((batch) => (
            <div key={batch.id}>
              <div className="px-4 py-2 flex items-center gap-2 bg-muted/20">
                <span className="orbit-eyebrow truncate">{batch.galleryName}</span>
                <span className="ml-auto text-[11px] tabular-nums text-muted-foreground shrink-0">
                  {batch.items.filter((i) => i.stage === 'done' || i.stage === 'processing').length}
                  {' / '}
                  {batch.items.length}
                </span>
              </div>
              {batch.items.map((item) => (
                <div key={item.id} className="px-4 py-2 text-sm">
                  <div className="flex items-center gap-3">
                    <span className="flex-1 truncate text-foreground/90 text-xs">{item.name}</span>
                    <StageLabel item={item} />
                    {/* FIX-2 — a failed video can be resent from here,
                        with no trip back to the file picker.
                        P2-1 — photos too: the same Retry now covers
                        both kinds, via the matching engine path. */}
                    {item.stage === 'failed' && (
                      <button
                        type="button"
                        onClick={() => {
                          const ok =
                            item.kind === 'video'
                              ? galleryUploads.retryVideo(item.id)
                              : galleryUploads.retryPhoto(item.id);
                          if (!ok) {
                            toast.error('This upload is no longer in the queue', {
                              description: `Add the ${item.kind} again from the gallery.`,
                            });
                          }
                        }}
                        className="shrink-0 inline-flex items-center gap-1 rounded-full border border-border/70 px-2 py-0.5 text-[10px] text-foreground/90 hover:bg-white/10"
                      >
                        <OrbitIcon name="arrow.counterclockwise" fallback={RefreshCcw} className="w-2.5 h-2.5" />
                        Retry
                      </button>
                    )}
                  </div>
                  {/* FIX-2 — the explanation has to be READABLE, not
                      buried in a title attribute. Every stalled or
                      failed row says what happened and what's next. */}
                  {(item.stage === 'stalled' || item.stage === 'failed') && item.message && (
                    <p
                      className={cn(
                        'mt-1 text-[10px] leading-snug',
                        item.stage === 'stalled'
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-destructive',
                      )}
                    >
                      {item.message}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ))}
          {!active && (
            <div className="px-4 py-2 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  galleryUploads.clearSettled();
                  setExpanded(false);
                }}
                className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => {
          if (allCleanAndSettled && expanded) {
            galleryUploads.clearSettled();
            setExpanded(false);
            return;
          }
          setExpanded((v) => !v);
        }}
        className={cn(
          'inline-flex items-center gap-2 pl-3 pr-2.5 py-2 rounded-full border shadow-lg backdrop-blur-md transition-colors',
          'orbit-box-dark bg-card/95 border-border/60 text-foreground hover:bg-card',
          failedTotal > 0 && !active && 'border-destructive/50',
        )}
        aria-label={expanded ? 'Collapse uploads' : 'Show uploads'}
      >
        {active || processingTotal > 0 ? (
          <NovaSpinner size={14} />
        ) : failedTotal > 0 ? (
          <OrbitIcon name="xmark" fallback={X} className="w-3.5 h-3.5 text-destructive" />
        ) : stalledTotal > 0 ? (
          <OrbitIcon name="clock" fallback={Clock} className="w-3.5 h-3.5 text-amber-500" />
        ) : (
          <OrbitIcon name="checkmark" fallback={Check} className="w-3.5 h-3.5 text-green-500" />
        )}
        <span className="text-xs font-medium tabular-nums">
          {active
            ? `Uploading ${snapshot.activeDone} / ${snapshot.activeTotal}`
            : failedTotal > 0
              ? `${failedTotal} failed`
              : processingTotal > 0
                ? `Transcoding ${processingTotal}`
                : stalledTotal > 0
                  ? `${stalledTotal} unconfirmed`
                  : 'Uploads complete'}
        </span>
        <OrbitIcon name="square.and.arrow.up" fallback={Upload} className="w-3 h-3 text-muted-foreground" />
        {expanded ? (
          <OrbitIcon name="chevron.down" fallback={ChevronDown} className="w-3.5 h-3.5 text-muted-foreground" />
        ) : (
          <OrbitIcon name="chevron.up" fallback={ChevronUp} className="w-3.5 h-3.5 text-muted-foreground" />
        )}
      </button>
    </div>
  );
}

export default UploadTray;
